import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Dashboard } from './components/Dashboard';
import { LandingPage } from './components/LandingPage';
import { FAQPage } from './components/FAQPage';
import { BlogPage } from './components/BlogPage';
import { AdminDashboard } from './components/AdminDashboard';
import { AdminAnalytics } from './components/AdminAnalytics';
import { WebGuideReader } from './components/WebGuideReader';
import { PaperTabs } from './components/PaperTabs';
import { TestSimulator } from './components/TestSimulator';
import { ReviewQueue } from './components/ReviewQueue';
import { StudyGuides } from './components/StudyGuides';
import { StudyResources } from './components/StudyResources';
import { CommunityForum } from './components/CommunityForum';
import { NotificationSettings } from './components/NotificationSettings';
import { PricingModal } from './components/PricingModal';
import { AuthModal } from './components/AuthModal';
import { OnboardingFlow, OnboardingResult, OnboardingNextStep } from './components/OnboardingFlow';
import { AppSidebar, AppTopBar } from './components/AppShell';
import { TutoringSection } from './components/TutoringSection';
import { ExamSidebar } from './components/ExamSidebar';
import { ExamModeSelectorModal } from './components/ExamModeSelectorModal';
import { MockExamSimulator } from './components/MockExamSimulator';
import { MockExamsHub } from './components/MockExamsHub';
import { PaymentConfirmation } from './components/PaymentConfirmation';
import { FloatingSupport } from './components/FloatingSupport';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Footer } from './components/Footer';
import { PrePraxisBadge } from './components/brand/PrePraxisBadge';
import { examCatalog, ExamCatalogEntry } from './data/examCatalog';
import { PRAXIS_PAPERS } from './data/praxisPapers';
import { UserProfile, TestAttempt, Question, PraxisPaper, ExamMode, MockExam, AuthIntent } from './types';
import { 
  initAuthUser, 
  getUserProfile, 
  saveUserProfile, 
  saveTestResult, 
  getUserTestResults, 
  getUserQuestionMastery,
  getPaperCatalogSummaries,
  DEFAULT_PROFILE,
  db,
  doc,
  onSnapshot,
  getLocalSessionToken,
  setLocalSessionToken,
  clearLocalSessionToken,
  assignActiveSession,
  signOutUser,
  isUserAdmin,
  isUserSubscribed,
  isPaperUnlockedForUser,
  accountNeedsEmailVerification,
  isNewAccountForOnboarding,
  ensureGuestPreviewClaimed
} from './lib/firebase';

// Short, shareable addresses used by the welcome email's buttons (and safe
// to bookmark). Each opens the matching tab, then the address bar is reset
// to "/" so later tab changes never leave a stale path behind.
const DEEP_LINK_TABS: Record<string, string> = {
  '/exams': 'papers',
  '/mock-exams': 'mock-exams',
  '/study-guides': 'guides',
  '/dashboard': 'dashboard',
  '/tutoring': 'tutoring'
};

export default function App() {
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [selectedExamEntry, setSelectedExamEntry] = useState<ExamCatalogEntry | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [testAttempts, setTestAttempts] = useState<TestAttempt[]>([]);
  const [selectedPaperForTest, setSelectedPaperForTest] = useState<string>('5713');
  // Null (not a default paper id) on purpose: a plain click on the top-nav
  // "Study Guides" tab should always land on the exam-family picker, while
  // handleOpenGuides (a paper's "Guide" button) sets this explicitly to
  // deep-link straight into that paper's guide.
  const [selectedPaperForGuide, setSelectedPaperForGuide] = useState<string | null>(null);
  // Web study guides (2026-10-28): /guides/<paperId>/<chapter> opens the
  // reader, so chapter links can be shared. webGuideIds lists the papers
  // that have one, loaded once from /api/guides.
  const [guidePaperId, setGuidePaperId] = useState<string | null>(null);
  const [guideChapter, setGuideChapter] = useState<number>(1);
  const [webGuideIds, setWebGuideIds] = useState<Set<string>>(new Set());
  const [selectedPaperForResources, setSelectedPaperForResources] = useState<string | null>(null);
  const [isPricingOpen, setIsPricingOpen] = useState<boolean>(false);
  const [isAuthOpen, setIsAuthOpen] = useState<boolean>(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signup');
  const [authIntent, setAuthIntent] = useState<AuthIntent | null>(null);
  const [modeSelectorPaper, setModeSelectorPaper] = useState<PraxisPaper | null>(null);
  const [activeMockExam, setActiveMockExam] = useState<MockExam | null>(null);
  const [mockExamsFamilyFilter, setMockExamsFamilyFilter] = useState<string | null>(null);
  const [allPapers, setAllPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  // Lightweight catalog (paper docs only, readable by every visitor) used for
  // exam-family paper counts and the onboarding exam picker. Unlike
  // allPapers, this never falls back to the small static list for guests.
  const [catalogPapers, setCatalogPapers] = useState<PraxisPaper[]>([]);

  // New sign-up flow (2026-10-07): verify email (new email/password
  // accounts only), then a one-time screener (exam, date, daily goal).
  const [verificationPending, setVerificationPending] = useState<boolean>(false);
  const [onboardingNewAccount, setOnboardingNewAccount] = useState<boolean>(false);

  // The blog post slug the real browser address currently points to, e.g.
  // "/blog/praxis-core-math-guide" -> "praxis-core-math-guide", or null when
  // showing the article list at "/blog". This is what gives each blog post
  // its own crawlable, shareable URL instead of only living behind a tab click.
  const [blogUrlSlug, setBlogUrlSlug] = useState<string | null>(null);
  // The very first run of the URL-sync effect below reads state from before
  // the initial-URL-parsing effect has applied, so it must be a no-op once.
  const skipNextBlogUrlSyncRef = React.useRef(true);

  const handleOpenAuthModal = (mode: 'signin' | 'signup' = 'signup', intent?: AuthIntent | null) => {
    setAuthModalMode(mode);
    setAuthIntent(intent || null);
    setIsAuthOpen(true);
  };

  const handleAuthSuccess = (profile: UserProfile, intent?: AuthIntent | null) => {
    const targetIntent = intent || authIntent;
    if (targetIntent) {
      if (targetIntent.actionType === 'exam' && targetIntent.exam) {
        setSelectedExamEntry(targetIntent.exam);
        setActiveTab('papers');
        window.location.hash = `#${targetIntent.exam.route}`;
      } else if (targetIntent.actionType === 'guides') {
        if (targetIntent.paperId) {
          setSelectedPaperForGuide(targetIntent.paperId);
        }
        setActiveTab('guides');
      } else if (targetIntent.actionType === 'community') {
        setActiveTab('forum');
      } else if (targetIntent.actionType === 'tutoring') {
        setActiveTab('tutoring');
      } else if (targetIntent.actionType === 'review') {
        setActiveTab('review');
      } else if (targetIntent.actionType === 'preview') {
        if (targetIntent.exam) {
          setSelectedExamEntry(targetIntent.exam);
          setActiveTab('papers');
          window.location.hash = `#${targetIntent.exam.route}`;
        } else {
          setActiveTab('papers');
        }
      } else if (targetIntent.actionType === 'pricing') {
        setIsPricingOpen(true);
      } else if (targetIntent.actionType === 'resume-test') {
        // Signed up (or logged in) from the free preview: stay in the
        // simulator, which reloads with the 5 bonus questions.
        if (targetIntent.paperId) setResumePaperId(targetIntent.paperId);
        setActiveTab('tests');
      } else if (targetIntent.tab) {
        setActiveTab(targetIntent.tab);
      } else {
        setActiveTab('dashboard');
      }
    } else {
      setActiveTab('dashboard');
    }
    setAuthIntent(null);
  };

  const openPricingModal = () => {
    if (!userProfile?.uid || userProfile.uid === 'guest_user_1') {
      handleOpenAuthModal('signup', { actionType: 'pricing' });
      return;
    }
    setIsPricingOpen(true);
  };

  // Spaced repetition & custom practice state
  const [dueReviewCount, setDueReviewCount] = useState<number>(0);
  const [customQuestions, setCustomQuestions] = useState<Question[] | undefined>(undefined);
  const [customTitle, setCustomTitle] = useState<string | undefined>(undefined);
  const [testMode, setTestMode] = useState<ExamMode>('timed');

  const refreshDueCount = async (uid?: string) => {
    if (!uid || uid === 'guest_user_1') return;
    try {
      const mastery = await getUserQuestionMastery(uid);
      if (Array.isArray(mastery)) {
        const nowMs = Date.now();
        const count = mastery.filter(m => m && m.nextReviewDue && new Date(m.nextReviewDue).getTime() <= nowMs).length;
        setDueReviewCount(count);
      }
    } catch (err) {
      console.warn('Error refreshing due review count:', err);
    }
  };

  // Initialize Auth & Profile from Firestore
  useEffect(() => {
    async function loadUser() {
      try {
        const user = await initAuthUser();
        if (user && user.uid && user.uid !== 'guest_user_1') {
          const profile = await getUserProfile(user.uid);
          if (profile) {
            profile.uid = user.uid;
            profile.email = profile.email || user.email || '';
            if (!profile.displayName || profile.displayName === 'Future Educator') {
              profile.displayName = user.displayName || (user.email ? user.email.split('@')[0] : 'PRAXIS Educator');
            }
          }
          const localToken = getLocalSessionToken();
          const remoteToken = profile?.activeSessionToken && typeof profile.activeSessionToken === 'string' && profile.activeSessionToken !== 'undefined' && profile.activeSessionToken !== 'null'
            ? profile.activeSessionToken.trim()
            : null;

          if (!localToken && remoteToken) {
            // Adopt existing token if local store was clean (e.g. initial reload)
            setLocalSessionToken(remoteToken);
          } else if (!localToken && !remoteToken) {
            // New authenticated user session without token: initialize one
            const freshToken = await assignActiveSession(user.uid);
            if (profile) {
              profile.activeSessionToken = freshToken;
            }
          }
          setUserProfile(profile || {
            ...DEFAULT_PROFILE,
            uid: user.uid,
            email: user.email || DEFAULT_PROFILE.email,
            displayName: user.displayName || (user.email ? user.email.split('@')[0] : DEFAULT_PROFILE.displayName)
          });
          const results = await getUserTestResults(user.uid);
          setTestAttempts(Array.isArray(results) ? results : []);
          await refreshDueCount(user.uid);
        }
      } catch (authErr) {
        console.warn('App: error initializing auth:', authErr);
      }

      try {
        const fp = await getPaperCatalogSummaries();
        if (Array.isArray(fp) && fp.length > 0) {
          setAllPapers(fp);
        }
      } catch (e) {
        console.warn('App: error fetching papers from Firestore:', e);
      }
    }
    loadUser();

    getPaperCatalogSummaries()
      .then((summaries) => {
        if (Array.isArray(summaries) && summaries.length > 0) setCatalogPapers(summaries);
      })
      .catch((catalogErr) => console.warn('App: error loading catalog summaries:', catalogErr));

    // Check for Paystack payment callback in URL query parameters or hash
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const reference = searchParams.get('reference') || searchParams.get('trxref');
      const isPaymentCallback = searchParams.get('payment_callback') === 'true';
      const hash = window.location.hash;
      
      if (reference || isPaymentCallback || hash.startsWith('#/payment/confirm')) {
        setActiveTab('payment-confirm');
      }
    } catch (urlErr) {
      console.warn('[App] Error reading URL parameters:', urlErr);
    }

    // Open directly to the blog list or a specific post when the page was
    // loaded straight at /blog or /blog/:slug -- a shared link, a bookmark,
    // or a search engine crawler, rather than only reachable via a nav click.
    try {
      const path = window.location.pathname;
      if (path === '/blog' || path === '/blog/') {
        setActiveTab('blog');
      } else if (path.startsWith('/blog/')) {
        const slugFromPath = decodeURIComponent(path.slice('/blog/'.length)).replace(/\/+$/, '');
        if (slugFromPath) {
          setActiveTab('blog');
          setBlogUrlSlug(slugFromPath);
        }
      }
    } catch (pathErr) {
      console.warn('[App] Error reading URL path:', pathErr);
    }

    // Shared study guide links: /guides/<paperId> or /guides/<paperId>/<n>.
    try {
      const gm = window.location.pathname.match(/^\/guides\/([A-Za-z0-9_-]+)(?:\/(\d+))?\/?$/);
      if (gm) {
        setGuidePaperId(gm[1]);
        setGuideChapter(gm[2] ? Math.max(1, Number(gm[2])) : 1);
        setActiveTab('guide');
      }
    } catch (guidePathErr) {
      console.warn('[App] Error reading guide link:', guidePathErr);
    }

    // Short links from the welcome email (e.g. prepraxis.com/study-guides),
    // and the "?verified=1" return address from the verification email.
    try {
      const cleanPath = window.location.pathname.replace(/\/+$/, '') || '/';
      const deepLinkTab = DEEP_LINK_TABS[cleanPath];
      const params = new URLSearchParams(window.location.search);
      if (deepLinkTab) {
        setActiveTab(deepLinkTab);
      }
      if (deepLinkTab || params.has('verified')) {
        params.delete('verified');
        const rest = params.toString();
        const nextPath = deepLinkTab ? '/' : window.location.pathname;
        window.history.replaceState({}, '', `${nextPath}${rest ? `?${rest}` : ''}${window.location.hash}`);
      }
    } catch (deepLinkErr) {
      console.warn('[App] Error reading deep link:', deepLinkErr);
    }
  }, []);

  // Which papers have a web guide (for the Guide buttons and Study Guides).
  useEffect(() => {
    fetch('/api/guides')
      .then((r) => (r.ok ? r.json() : { guides: [] }))
      .then((b) => setWebGuideIds(new Set((b.guides || []).map((g: any) => String(g.paperId)))))
      .catch(() => {});
  }, []);

  // Keep the address bar on /guides/<paperId>/<n> while reading, so the
  // link can be copied, and clear it when the reader is left.
  useEffect(() => {
    if (activeTab === 'guide' && guidePaperId) {
      const target = `/guides/${encodeURIComponent(guidePaperId)}/${guideChapter}`;
      if (window.location.pathname !== target) window.history.replaceState({ tab: 'guide' }, '', target);
    } else if (window.location.pathname.startsWith('/guides/')) {
      window.history.replaceState({}, '', '/');
    }
  }, [activeTab, guidePaperId, guideChapter]);

  const openWebGuide = (paperId: string, chapter = 1) => {
    setGuidePaperId(paperId);
    setGuideChapter(chapter);
    setActiveTab('guide');
  };

  // Keep the blog view in sync with the browser's back/forward buttons.
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/blog' || path === '/blog/') {
        setActiveTab('blog');
        setBlogUrlSlug(null);
      } else if (path.startsWith('/blog/')) {
        const slugFromPath = decodeURIComponent(path.slice('/blog/'.length)).replace(/\/+$/, '');
        setActiveTab('blog');
        setBlogUrlSlug(slugFromPath || null);
      } else {
        setBlogUrlSlug(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Push a real address whenever the blog tab or the open post changes, so
  // /blog and /blog/:slug are genuine, bookmarkable, crawlable URLs.
  useEffect(() => {
    if (skipNextBlogUrlSyncRef.current) {
      skipNextBlogUrlSyncRef.current = false;
      return;
    }
    if (activeTab === 'blog') {
      const targetPath = blogUrlSlug ? `/blog/${encodeURIComponent(blogUrlSlug)}` : '/blog';
      if (window.location.pathname !== targetPath) {
        window.history.pushState({ tab: 'blog', slug: blogUrlSlug }, '', targetPath);
      }
    } else if (window.location.pathname.startsWith('/blog')) {
      window.history.pushState({}, '', '/');
      setBlogUrlSlug(null);
    }
  }, [activeTab, blogUrlSlug]);

  // Real-time Single-Device Active Session Enforcement and Subscription Sync
  useEffect(() => {
    if (!userProfile?.uid || userProfile.uid === 'guest_user_1') {
      return;
    }

    const userDocRef = doc(db, 'users', userProfile.uid);
    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const remoteData = snapshot.data() as UserProfile | undefined;
      if (!remoteData) return;

      // Real-time synchronization of subscription status, tier, and unlocked papers
      setUserProfile(prev => {
        if (
          prev.tier !== remoteData.tier ||
          prev.hasFullAccess !== remoteData.hasFullAccess ||
          prev.subscriptionStatus !== remoteData.subscriptionStatus ||
          prev.subscriptionEndDate !== remoteData.subscriptionEndDate ||
          (remoteData.unlockedPaperIds && remoteData.unlockedPaperIds.length !== (prev.unlockedPaperIds?.length || 0))
        ) {
          console.log(`[App] Real-time profile update received for ${userProfile.uid}: tier=${remoteData.tier}, hasFullAccess=${remoteData.hasFullAccess}, status=${remoteData.subscriptionStatus}`);
          return {
            ...prev,
            ...remoteData,
            uid: remoteData.uid || prev.uid,
            email: remoteData.email || prev.email,
            tier: remoteData.tier || prev.tier,
            hasFullAccess: remoteData.hasFullAccess ?? prev.hasFullAccess,
            unlockedPaperIds: remoteData.unlockedPaperIds || prev.unlockedPaperIds
          };
        }
        return prev;
      });

      // Skip single-device termination for admin or free accounts
      if (isUserAdmin(userProfile) || userProfile.tier === 'free') {
        return;
      }

      const remoteToken = typeof remoteData.activeSessionToken === 'string' && remoteData.activeSessionToken !== 'undefined' && remoteData.activeSessionToken !== 'null'
        ? remoteData.activeSessionToken.trim()
        : null;

      if (remoteToken) {
        const localToken = getLocalSessionToken();
        // If this device holds a local token and the remote profile's token has changed,
        // it means another device has logged in with this subscriber account.
        if (localToken && remoteToken !== localToken) {
          const remoteShort = remoteToken.length > 8 ? remoteToken.substring(0, 8) : remoteToken;
          const localShort = typeof localToken === 'string' && localToken.length > 8 ? localToken.substring(0, 8) : String(localToken);
          console.warn(`[Single-Device Enforcement] Active session token mismatch for Subscribed UID ${userProfile?.uid || 'unknown'}. Remote: ${remoteShort}..., Local: ${localShort}... Terminating session silently.`);
          clearLocalSessionToken();
          signOutUser().catch(() => {});
          setUserProfile(DEFAULT_PROFILE);
          setIsAuthOpen(true); // Silently present sign-in view
        }
      }
    }, (err) => {
      console.warn('[Single-Device Enforcement] Error in active session listener:', err);
    });

    return () => {
      unsubscribe();
    };
  }, [userProfile?.uid, userProfile?.tier, userProfile?.role, userProfile?.email]);

  // Listen to hash routes e.g. #/exams/california or #/payment/confirm
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#/exams/')) {
        const slug = hash.replace('#/exams/', '').split('?')[0];
        const match = examCatalog.find(e => e.id === slug || e.route.endsWith(slug));
        if (match) {
          setSelectedExamEntry(match);
          setActiveTab('papers');
        }
      } else if (hash.startsWith('#/payment/confirm')) {
        setActiveTab('payment-confirm');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Re-evaluate the verification gate whenever a different account signs in.
  useEffect(() => {
    if (!userProfile?.uid || userProfile.uid === 'guest_user_1') {
      setVerificationPending(false);
      setOnboardingNewAccount(false);
      return;
    }
    setVerificationPending(accountNeedsEmailVerification());
    setOnboardingNewAccount(isNewAccountForOnboarding());
    // If this browser tried questions as a guest, carry them over now.
    ensureGuestPreviewClaimed();
  }, [userProfile?.uid]);

  // Paper a guest was trying when they signed up from the free preview.
  // The screener starts with it selected.
  const [resumePaperId, setResumePaperId] = useState<string | null>(null);

  const isSignedInStudent = !!userProfile?.uid && userProfile.uid !== 'guest_user_1' && !isUserAdmin(userProfile);
  const needsScreener = isSignedInStudent && !userProfile.targetPaperId && !userProfile.onboardingCompletedAt;
  // Once the screener opens it stays open until the student picks a next
  // step on the finished plan, even if the profile updates underneath it.
  const [onboardingHold, setOnboardingHold] = useState(false);
  const showOnboarding = !isAuthOpen && isSignedInStudent && (verificationPending || needsScreener || onboardingHold);
  useEffect(() => {
    if (!isAuthOpen && isSignedInStudent && needsScreener && !verificationPending) setOnboardingHold(true);
  }, [isAuthOpen, isSignedInStudent, needsScreener, verificationPending]);

  const handleOnboardingComplete = (result: OnboardingResult, next: OnboardingNextStep = 'exam') => {
    setOnboardingHold(false);
    setResumePaperId(null);
    setUserProfile(prev => ({
      ...prev,
      targetPaperId: result.targetPaperId ?? prev.targetPaperId,
      targetExamFamily: result.targetExamFamily || prev.targetExamFamily,
      targetExamDate: result.skipped ? prev.targetExamDate : result.targetExamDate,
      dailyGoalMinutes: result.dailyGoalMinutes ?? prev.dailyGoalMinutes,
      onboardingCompletedAt: result.onboardingCompletedAt
    }));
    const entry = !result.skipped && result.targetExamFamily
      ? examCatalog.find(e => e.id === result.targetExamFamily)
      : undefined;
    const chosenPaper = catalogOrAll.find(p => p.id === result.targetPaperId);
    const chosenIsBundle = !!chosenPaper && Array.isArray(chosenPaper.bundleOf) && chosenPaper.bundleOf.length > 0;
    const chosenPaperId = result.targetPaperId && !chosenIsBundle && !examCatalog.some(e => e.id === result.targetPaperId)
      ? result.targetPaperId
      : undefined;
    if (next === 'practice' && chosenPaperId) {
      // Straight into the chosen exam. Subscribers get the normal mode
      // picker; free accounts open study mode, where the simulator serves
      // their 5 free preview questions through the server.
      if (entry) setSelectedExamEntry(entry);
      if (isPaperUnlockedForUser(userProfile, chosenPaperId)) {
        setActiveTab('papers');
        handleStartTest(chosenPaperId);
      } else {
        setSelectedPaperForTest(chosenPaperId);
        setCustomQuestions(undefined);
        setCustomTitle(undefined);
        setTestMode('study');
        setActiveTab('tests');
      }
    } else if (next !== 'dashboard' && entry) {
      // Land on the chosen exam's page (Start Practice, Mock Exams, Guide
      // and Flashcards for each paper).
      setSelectedExamEntry(entry);
      setActiveTab('papers');
      window.location.hash = `#${entry.route}`;
    } else {
      setActiveTab('dashboard');
    }
    window.scrollTo?.(0, 0);
  };

  const handleOnboardingSignOut = async () => {
    try {
      await signOutUser();
    } catch (err) {
      console.warn('[Onboarding] Sign out error:', err);
    }
    setUserProfile(DEFAULT_PROFILE);
    setVerificationPending(false);
    setOnboardingHold(false);
    setActiveTab('dashboard');
  };

  // ---- Logged-in app shell (2026-10-07 navy redesign) ----
  const isSignedIn = !!userProfile?.uid && userProfile.uid !== 'guest_user_1';
  const catalogOrAll = catalogPapers.length > 0 ? catalogPapers : allPapers;

  const findGoalFamily = (): ExamCatalogEntry | undefined => {
    if (userProfile.targetExamFamily) {
      const byFamily = examCatalog.find(e => e.id === userProfile.targetExamFamily);
      if (byFamily) return byFamily;
    }
    if (userProfile.targetPaperId) {
      const byId = examCatalog.find(e => e.id === userProfile.targetPaperId);
      if (byId) return byId;
      const paper = catalogOrAll.find(p => p.id === userProfile.targetPaperId);
      if (paper) {
        const code = String(paper.examCode || '').toLowerCase();
        if (code.startsWith('cbest') || code.startsWith('cset') || String(paper.code || '').toLowerCase().startsWith('fort')) {
          return examCatalog.find(e => e.id === 'california');
        }
        if (code === 'parapathways') return examCatalog.find(e => e.id === 'paraprofessional-pathway');
        return examCatalog.find(e => e.id === 'most-other-states');
      }
    }
    return undefined;
  };

  const goToDirectory = () => {
    setSelectedExamEntry(null);
    window.location.hash = '';
    setActiveTab('papers');
  };

  const handleOpenGoalExam = () => {
    const family = findGoalFamily();
    if (family) handleSelectExam(family);
    else goToDirectory();
  };

  const handleOpenFlashcards = () => {
    const goalId = userProfile.targetPaperId;
    if (goalId && catalogOrAll.some(p => p.id === goalId)) {
      handleOpenResources(goalId);
    } else {
      // No goal paper yet: flashcards live under each paper, so start from the directory.
      goToDirectory();
    }
  };

  const handleSearchExams = (query: string) => {
    const q = query.toLowerCase();
    if (q) {
      const family = examCatalog.find(e =>
        `${e.state} ${e.shortLabel} ${e.examLabel} ${e.examCodes.join(' ')}`.toLowerCase().includes(q)
      );
      if (family) {
        handleSelectExam(family);
        return;
      }
      const paper = catalogOrAll.find(p => `${p.code} ${p.title}`.toLowerCase().includes(q));
      if (paper) {
        handleStartTest(paper.id);
        return;
      }
    }
    goToDirectory();
  };

  const shellNavProps = {
    activeTab,
    setActiveTab: (tab: string) => {
      if (tab === 'papers') {
        goToDirectory();
        return;
      }
      setActiveTab(tab);
    },
    userProfile,
    dueReviewCount,
    openPricingModal,
    onOpenFlashcards: handleOpenFlashcards,
    onOpenGoalExam: handleOpenGoalExam
  };

  const handleSelectExam = (entry: ExamCatalogEntry) => {
    setSelectedExamEntry(entry);
    setActiveTab('papers');
    window.location.hash = `#${entry.route}`;
  };

  const handleNavigateToMockExams = (familyOrState: string) => {
    setMockExamsFamilyFilter(familyOrState);
    setActiveTab('mock-exams');
  };

  const handleStartTest = (paperId: string, mode?: ExamMode) => {
    if (!isPaperUnlockedForUser(userProfile, paperId)) {
      openPricingModal();
      return;
    }

    if (mode) {
      setSelectedPaperForTest(paperId);
      setCustomQuestions(undefined);
      setCustomTitle(undefined);
      setTestMode(mode);
      setActiveTab('tests');
      return;
    }

    const matched = allPapers.find(p => p.id === paperId || p.code === paperId) || 
                    PRAXIS_PAPERS.find(p => p.id === paperId || p.code === paperId);
    if (matched) {
      setModeSelectorPaper(matched);
    } else {
      setSelectedPaperForTest(paperId);
      setCustomQuestions(undefined);
      setCustomTitle(undefined);
      setTestMode('timed');
      setActiveTab('tests');
    }
  };

  // "Try 5 free questions" on a locked paper. No sign up needed: the
  // simulator serves the free preview from the server (guests get 5 in
  // total, free accounts 5 per paper) and asks guests to sign up after.
  const handleTryFree = (paperId: string) => {
    if (isPaperUnlockedForUser(userProfile, paperId)) {
      handleStartTest(paperId);
      return;
    }
    setSelectedPaperForTest(paperId);
    setCustomQuestions(undefined);
    setCustomTitle(undefined);
    setTestMode('study');
    setActiveTab('tests');
    window.scrollTo?.(0, 0);
  };

  const handleStartCustomPractice = (questions: Question[], title: string, mode?: ExamMode | 'practice' | 'exam') => {
    setCustomQuestions(questions);
    setCustomTitle(title);
    setTestMode(mode === 'practice' ? 'study' : mode === 'exam' ? 'timed' : (mode || 'study'));
    setActiveTab('tests');
  };

  const handleOpenGuides = (paperId: string) => {
    // A web guide always opens: chapter 1 is free and the rest is gated
    // by the server.
    if (webGuideIds.has(paperId)) {
      openWebGuide(paperId);
      return;
    }
    if (!isPaperUnlockedForUser(userProfile, paperId)) {
      openPricingModal();
      return;
    }
    setSelectedPaperForGuide(paperId);
    setActiveTab('guides');
  };

  const handleOpenResources = (paperId: string) => {
    if (!isPaperUnlockedForUser(userProfile, paperId)) {
      openPricingModal();
      return;
    }
    setSelectedPaperForResources(paperId);
    setActiveTab('resources');
  };

  const handleFinishTest = async (attempt: TestAttempt) => {
    setTestAttempts(prev => [attempt, ...prev]);
    await saveTestResult(attempt);
    if (userProfile.uid) {
      await refreshDueCount(userProfile.uid);
    }

    // Update study time in profile
    const minutesAdded = Math.round(attempt.timeSpentSeconds / 60) || 5;
    setUserProfile(prev => {
      const updated = {
        ...prev,
        totalStudyMinutes: prev.totalStudyMinutes + minutesAdded
      };
      saveUserProfile(updated);
      return updated;
    });
  };

  return (
    <div className={`min-h-screen ${isSignedIn ? 'bg-[#f5f6fb]' : 'bg-slate-50'} text-slate-900 flex flex-col font-sans antialiased selection:bg-indigo-500 selection:text-white`}>
      {/* Logged-in: navy left sidebar (2026-10-07 redesign) */}
      {isSignedIn && <AppSidebar {...shellNavProps} />}

      <div className={isSignedIn ? 'lg:pl-[260px] flex-1 flex flex-col min-w-0' : 'contents'}>
      {/* Top header: slim app bar when signed in, marketing navbar otherwise */}
      {isSignedIn ? (
        <AppTopBar
          {...shellNavProps}
          onOpenAccount={() => handleOpenAuthModal('signin')}
          onSearchExams={handleSearchExams}
        />
      ) : (
      <Navbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        userProfile={userProfile}
        setUserProfile={setUserProfile}
        openPricingModal={openPricingModal}
        openAuthModal={handleOpenAuthModal}
        dueReviewCount={dueReviewCount}
        selectedExamEntry={selectedExamEntry}
        onSelectExam={handleSelectExam}
      />
      )}

      {/* Main Content Router */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col lg:flex-row gap-6">
        {/* Left Exam Sidebar Navigation (Visible on Papers/Exams tab or collapsible drawer) */}
        {activeTab === 'papers' && !isSignedIn && (
          <ExamSidebar
            currentRoute={selectedExamEntry ? selectedExamEntry.route : ''}
            onSelectExam={handleSelectExam}
            onViewAllExams={() => {
              setSelectedExamEntry(null);
              window.location.hash = '';
            }}
          />
        )}

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          {activeMockExam ? (
            <MockExamSimulator
              mockExam={activeMockExam}
              userProfile={userProfile}
              onExit={() => setActiveMockExam(null)}
              openPricingModal={openPricingModal}
            />
          ) : (
            <>
              {activeTab === 'landing' && (
                <LandingPage
                  onOpenAuthModal={handleOpenAuthModal}
                  setActiveTab={setActiveTab}
                  onOpenPricingModal={openPricingModal}
                  onSelectExam={handleSelectExam}
                  userProfile={userProfile}
                  papers={catalogPapers}
                />
              )}

              {activeTab === 'analytics' && (isUserAdmin(userProfile) || userProfile.uid === 'GBQme94c03e6XCBHGQEqF2eFBrF3') && (
                <ErrorBoundary fallbackTitle="Analytics Error">
                  <AdminAnalytics />
                </ErrorBoundary>
              )}

              {activeTab === 'dashboard' && (
                (isUserAdmin(userProfile) || userProfile.uid === 'GBQme94c03e6XCBHGQEqF2eFBrF3') ? (
                  <ErrorBoundary fallbackTitle="Admin Dashboard Error">
                    <AdminDashboard adminProfile={userProfile} />
                  </ErrorBoundary>
                ) : (userProfile.uid && userProfile.uid !== 'guest_user_1') ? (
                  <ErrorBoundary fallbackTitle="Dashboard Error">
                    <Dashboard
                      userProfile={userProfile}
                      testAttempts={testAttempts}
                      onStartTest={handleStartTest}
                      onOpenGuides={handleOpenGuides}
                      openPricingModal={openPricingModal}
                      setActiveTab={setActiveTab}
                      dueReviewCount={dueReviewCount}
                      onStartCustomPractice={handleStartCustomPractice}
                      onSelectExam={handleSelectExam}
                    />
                  </ErrorBoundary>
                ) : (
                  <LandingPage
                    onOpenAuthModal={handleOpenAuthModal}
                    setActiveTab={setActiveTab}
                    onOpenPricingModal={openPricingModal}
                    onSelectExam={handleSelectExam}
                    userProfile={userProfile}
                    papers={catalogPapers}
                  />
                )
              )}

              {activeTab === 'faq' && (
                <FAQPage
                  userProfile={userProfile}
                  setActiveTab={setActiveTab}
                  openAuthModal={handleOpenAuthModal}
                  openPricingModal={openPricingModal}
                />
              )}

              {activeTab === 'blog' && (
                <BlogPage
                  onSelectExam={handleSelectExam}
                  setActiveTab={setActiveTab}
                  onOpenAuthModal={handleOpenAuthModal}
                  urlSlug={blogUrlSlug}
                  onSlugChange={setBlogUrlSlug}
                />
              )}

              {activeTab === 'papers' && (
                <PaperTabs
                  userProfile={userProfile}
                  onStartTest={handleStartTest}
                  onNavigateToMockExams={handleNavigateToMockExams}
                  onOpenGuides={handleOpenGuides}
                  onOpenResources={handleOpenResources}
                  onTryFree={handleTryFree}
                  openPricingModal={openPricingModal}
                  selectedExamEntry={selectedExamEntry}
                  onSelectExam={handleSelectExam}
                  onClearSelectedExam={() => {
                    setSelectedExamEntry(null);
                    window.location.hash = '';
                  }}
                  catalogPapers={catalogPapers}
                  testAttempts={testAttempts}
                  dueReviewCount={dueReviewCount}
                  onOpenTutoring={() => setActiveTab('tutoring')}
                  onOpenReview={() => setActiveTab('review')}
                />
              )}

              {activeTab === 'mock-exams' && (
                <MockExamsHub
                  userProfile={userProfile}
                  onStartMockExam={(mock) => {
                    if (!isPaperUnlockedForUser(userProfile, mock.paperId) && !isUserAdmin(userProfile)) {
                      openPricingModal();
                      return;
                    }
                    setActiveMockExam(mock);
                  }}
                  openPricingModal={openPricingModal}
                  initialFamilyFilter={mockExamsFamilyFilter}
                  onSelectExamDirectory={(_familyOrState) => {
                    setActiveTab('papers');
                  }}
                  onOpenGuides={handleOpenGuides}
                />
              )}

              {activeTab === 'review' && (
                <ReviewQueue
                  userProfile={userProfile}
                  onStartCustomPractice={handleStartCustomPractice}
                  onOpenPricingModal={openPricingModal}
                />
              )}

              {activeTab === 'tests' && (
                <TestSimulator
                  paperId={selectedPaperForTest}
                  userProfile={userProfile}
                  onFinishTest={handleFinishTest}
                  onBackToPapers={() => setActiveTab('papers')}
                  customQuestions={customQuestions}
                  customTitle={customTitle}
                  mode={testMode}
                  openPricingModal={openPricingModal}
                  onRequestSignup={(mode) =>
                    handleOpenAuthModal(mode, { actionType: 'resume-test', paperId: selectedPaperForTest })
                  }
                />
              )}

              {activeTab === 'guides' && (
                <StudyGuides
                  initialPaperId={selectedPaperForGuide}
                  onStartTest={handleStartTest}
                  userProfile={userProfile}
                  openPricingModal={openPricingModal}
                  webGuideIds={webGuideIds}
                  onOpenWebGuide={(pid) => openWebGuide(pid)}
                />
              )}

              {activeTab === 'guide' && guidePaperId && (
                <ErrorBoundary fallbackTitle="Study Guide Error">
                  <WebGuideReader
                    key={guidePaperId}
                    paperId={guidePaperId}
                    initialChapter={guideChapter}
                    userProfile={userProfile}
                    onChapterChange={setGuideChapter}
                    onOpenAuthModal={(mode) => handleOpenAuthModal(mode === 'login' ? 'signin' : 'signup')}
                    openPricingModal={openPricingModal}
                    onStartTest={handleStartTest}
                    onBack={() => setActiveTab('guides')}
                  />
                </ErrorBoundary>
              )}

              {activeTab === 'resources' && selectedPaperForResources && (
                <StudyResources
                  paperId={selectedPaperForResources}
                  onStartTest={handleStartTest}
                  userProfile={userProfile}
                  openPricingModal={openPricingModal}
                  onBack={() => setActiveTab('papers')}
                />
              )}

              {activeTab === 'forum' && (
                <CommunityForum userProfile={userProfile} />
              )}

              {activeTab === 'tutoring' && (
                <TutoringSection
                  userProfile={userProfile}
                  openPricingModal={openPricingModal}
                />
              )}

              {activeTab === 'reminders' && (
                <NotificationSettings
                  userProfile={userProfile}
                  setUserProfile={setUserProfile}
                />
              )}

              {activeTab === 'payment-confirm' && (
                <PaymentConfirmation
                  userProfile={userProfile}
                  setUserProfile={setUserProfile}
                  onNavigateToDashboard={() => {
                    setActiveTab('dashboard');
                    window.location.hash = '';
                  }}
                  openPricingModal={openPricingModal}
                />
              )}
            </>
          )}
        </main>
    </div>

      {/* Unified Single Footer Component across all views */}
      {!activeMockExam && activeTab !== 'tests' && (
        <Footer
          setActiveTab={setActiveTab}
          onSelectExam={handleSelectExam}
          onOpenPricingModal={openPricingModal}
          onOpenAuthModal={handleOpenAuthModal}
          userProfile={userProfile}
        />
      )}
      </div>

      {/* Floating Support (WhatsApp & Live Help) */}
      <FloatingSupport
        onOpenPricingModal={openPricingModal}
        onOpenAuthModal={(mode) => handleOpenAuthModal(mode || 'signup')}
        userProfile={userProfile}
        papers={catalogOrAll}
        activeTab={activeTab}
        selectedExamEntry={selectedExamEntry}
        onTryFree={handleTryFree}
        onStartTest={handleStartTest}
        onSelectExam={handleSelectExam}
        setActiveTab={(tab) => {
          if (tab === 'papers') {
            setSelectedExamEntry(null);
            window.location.hash = '';
          }
          setActiveTab(tab);
        }}
      />

      {/* Pricing Upgrade Modal */}
      {isPricingOpen && (
        <PricingModal
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          onClose={() => setIsPricingOpen(false)}
        />
      )}

      {/* New sign-up flow: email verification + one-time screener */}
      {showOnboarding && (
        <OnboardingFlow
          userProfile={userProfile}
          papers={catalogOrAll}
          needsVerification={verificationPending}
          isNewAccount={onboardingNewAccount}
          onVerified={() => setVerificationPending(false)}
          onComplete={handleOnboardingComplete}
          onSignOut={handleOnboardingSignOut}
          presetPaperId={resumePaperId}
        />
      )}

      {/* Auth & Profile Modal */}
      <AuthModal
        isOpen={isAuthOpen}
        initialMode={authModalMode}
        intent={authIntent}
        onClose={() => {
          setIsAuthOpen(false);
          setAuthIntent(null);
        }}
        userProfile={userProfile}
        onProfileUpdate={(updated) => setUserProfile(updated)}
        onAuthSuccess={handleAuthSuccess}
      />

      {/* Exam Mode Selector Modal */}
      {modeSelectorPaper && (
        <ExamModeSelectorModal
          paper={modeSelectorPaper}
          isOpen={true}
          onClose={() => setModeSelectorPaper(null)}
          onSelectMode={(selectedMode) => {
            const pId = modeSelectorPaper.id;
            setModeSelectorPaper(null);
            handleStartTest(pId, selectedMode);
          }}
        />
      )}
    </div>
  );
}
